/* =============================================================================
 * Filter model — shared helpers for the table filtering system.
 *
 * A "field" describes something you can filter on:
 *   { id, label, icon?, type, options? }
 *   type: 'select' | 'multiselect' | 'daterange' | 'date' | 'numberrange' | 'text'
 *   options: [{ value, label }]   (select / multiselect)
 *
 * A "values" map holds the current filter state keyed by field id, where the
 * value shape depends on the field type:
 *   select        -> string
 *   multiselect   -> string[]
 *   daterange     -> { from, to }
 *   date          -> string (yyyy-mm-dd)
 *   numberrange   -> { min, max }
 *   text          -> string
 * ========================================================================== */

import { t } from '../../i18n';

export const emptyValueFor = (field) => {
  switch (field.type) {
    case 'multiselect': return [];
    case 'daterange': return { from: '', to: '' };
    case 'numberrange': return { min: '', max: '' };
    default: return '';
  }
};

export const isFilterEmpty = (field, value) => {
  if (value == null) return true;
  switch (field.type) {
    case 'multiselect': return value.length === 0;
    case 'daterange': return !value.from && !value.to;
    case 'numberrange': return (value.min === '' || value.min == null) && (value.max === '' || value.max == null);
    default: return value === '';
  }
};

const labelFor = (field, val) => {
  const opt = (field.options || []).find((o) => String(o.value) === String(val));
  return opt ? opt.label : val;
};

/** Human-readable summary of a value, for chip / column display. */
export const formatFilterValue = (field, value) => {
  if (isFilterEmpty(field, value)) return '';
  switch (field.type) {
    case 'multiselect':
      return value.length === 1 ? labelFor(field, value[0]) : t('{count} selected', { count: value.length });
    case 'select':
      return labelFor(field, value);
    case 'daterange': {
      const { from, to } = value;
      if (from && to) return `${from} → ${to}`;
      if (from) return t('from {date}', { date: from });
      return t('until {date}', { date: to });
    }
    case 'numberrange': {
      const { min, max } = value;
      const hasMin = min !== '' && min != null;
      const hasMax = max !== '' && max != null;
      if (hasMin && hasMax) return `${min}–${max}`;
      if (hasMin) return `≥ ${min}`;
      return `≤ ${max}`;
    }
    default:
      return String(value);
  }
};

/** Build the chip list for the active (non-empty) filters, preserving field order. */
export const activeFilterChips = (fields, values) =>
  fields
    .filter((f) => !isFilterEmpty(f, values?.[f.id]))
    .map((f) => ({ id: f.id, label: f.label, value: formatFilterValue(f, values[f.id]) }));
